<template>
  <vddl-list
    :id="id"
    class="jumper"
    :class="classObject"
    :style="styles"
    :list="tlist"
    :allowed-types="allowedTypes"
    :drop="onJumperDropped"
    :disable-if="!canEdit"
  >
    <div
      class="wrapper"
      @contextmenu.prevent="onContextMenu"
      @mouseup="onMouseUp"
      @mousedown="onMouseDown"
    >
      <div
        v-if="canEdit"
        ref="leftTarget"
        class="connection-handler left"
        data-handler="left"
      />
      <div
        v-if="canEdit"
        ref="rightTarget"
        class="connection-handler right"
        data-handler="right"
      />
      <div
        v-if="canEdit"
        ref="topTarget"
        class="connection-handler up"
        data-handler="top"
      />
      <div
        v-if="canEdit"
        ref="bottomTarget"
        class="connection-handler down"
        data-handler="bottom"
      />
      <div class="handler">
        <svg-icon
          v-if="element"
          :fill="theme"
          :width="'24'"
          :height="'86'"
          icon="app-icons/jump"
        />
        <div class="container" :class="{ empty: !element }">
          <div class="row">
            <div
              :class="{ empty: !element, active: element }"
              class="title"
              @click="onClickTitle"
            >
              {{ element ? element.title : 'Empty jump target' }}
            </div>
          </div>
          <div class="board">
            {{ element ? 'in ' + element.boardName : 'Drop element to link' }}
          </div>
        </div>
      </div>
    </div>
  </vddl-list>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import _ from 'lodash';
import jspSingleton from '@/lib/jspSingleton.js';
import { useProtect } from '@/composables/useProtect.js';
import {
  SYS_PERMISSIONS,
  EVENT,
  DRAG_TYPE,
  DRAG_LIST,
  DRAG_TYPES_NEW,
} from '@/constants.js';
import misc from '../mixins/misc.js';
import SvgIcon from '../all/SvgIcon.vue';
import updater from '../../vuex/project/updater.js';
import { getTextContent } from '../../utils.js';
import { OPERATION, TYPE } from '../../vuex/application/clipboard.js';

export default {
  components: {
    SvgIcon,
  },

  mixins: [misc],

  props: {
    id: {
      type: String,
      default: null,
    },
    virtualSelected: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      dragEnter: false,
      allowedTypes: [DRAG_TYPE.BOARD_ELEMENT, DRAG_TYPE.SEARCH_ELEMENT],
      tlist: [],
      dragCounter: 0,
      themes: {
        default: '#354042',
        red: '#571400',
        blue: '#002e6b',
        purple: '#540093',
        cyan: '#006557',
        green: '#00500b',
        pink: '#69003f',
        gold: '#5d5a00',
        orange: '#742b00',
        lightBlue: '#005186',
        brown: '#563400',
        moss: '#386300',
      },
    };
  },

  computed: {
    ...mapGetters('project/data/elements', ['getContainerNode']),
    ...mapGetters('project/data/jumpers', { getJumper: 'getNode' }),
    ...mapGetters('project/data/elements', { getElement: 'getNode' }),
    ...mapGetters('project/data/notes', { getNote: 'getNode' }),
    ...mapGetters('project/data/branches', { getBranch: 'getNode' }),
    ...mapGetters('project/data/comments', { getComment: 'getNode' }),
    ...mapGetters('project/config/jumpers', {
      getIsSelected: 'getIsSelected',
      getIsJumperSelectionEmpty: 'getIsSelectionEmpty',
      getJumperSelections: 'getSelections',
    }),
    ...mapGetters('project/config/connections', {
      getIsConnectionSelectionEmpty: 'getIsSelectionEmpty',
    }),
    ...mapGetters('application/channels', ['getCollaborators']),
    ...mapGetters('project/config/boards', ['getOpen']),
    ...mapGetters('project/data/boards', { getBoard: 'getNode' }),
    ...mapGetters('application/drag', ['getItems', 'getTarget', 'getIsEmpty']),
    ...mapGetters('project/config', ['getGrid']),
    ...mapGetters('application', ['getExactTransform']),

    theme() {
      if (!this.element) return '#0d0e0e';
      const hasColor = _.has(this.themes, this.element.theme);
      if (hasColor) {
        return this.themes[this.element.theme];
      }
      return this.themes.default;
    },

    /**
     * Return the jumper
     * @returns {Object}    The jumper object
     */
    jumper() {
      return this.getJumper(this.id);
    },

    /**
     * Return the referenced element data (stripped)
     * @returns {Object}    The element object
     */
    element() {
      if (!this.jumper) return null;

      const element = this.getElement(this.jumper.elementId);

      if (element) {
        const { x, y, width, height, theme } = element;
        const title = getTextContent(element.title) || 'Empty element';

        const board = this.getContainerNode(
          this.jumper.elementId,
          'boards',
          'elements'
        );
        const boardName = board.name;
        const boardId = board.id;

        return {
          x,
          y,
          width,
          height,
          title,
          boardName,
          boardId,
          theme,
        };
      }
      return null;
    },

    /**
     * Return the css styles for positioning
     * @returns {Object}    The { left, top, --user-selected-color } styles
     */
    styles() {
      const { x, y } = this.jumper || { x: 0, y: 0 };
      return {
        left: `${x}px`,
        top: `${y}px`,
        '--user-selected-color': this.selectedByCollaborator?.color,
      };
    },

    /**
     * Return the connection handler css styles for scaling
     * @returns {Object}    The { transform } styles
     */
    // connectionHandlerStyles() {
    //   const distance = 16 / this.scale;
    //   return {
    //     width: `${distance}px`,
    //     height: `${distance}px`,
    //     left: `${-distance}px`,
    //     right: `${-distance}px`,
    //     top: `${-distance}px`,
    //     bottom: `${-distance}px`,
    //     // 'border-radius': `${8 / this.scale}px`,
    //   };
    // },

    /**
     * Get the scale of the exact transform
     * @returns {Number}
     */
    scale() {
      return this.getExactTransform.scale;
    },

    /**
     * Return the collaboarator that has selected this
     */
    selectedByCollaborator() {
      const collaborators = this.getCollaborators.filter(
        c =>
          c.selections &&
          c.selections.jumpers &&
          c.selections.jumpers.includes(this.id)
      );
      if (collaborators.length > 0) return collaborators[0];
      return null;
    },

    /**
     * Return whether the current jumper is selected
     * @returns {Boolean}   Whether it is selected
     */
    isSelected() {
      return this.getIsSelected(this.id);
    },

    /**
     * Return whether there is something currently selected
     * @returns {Boolean}   Whether there is something selected
     */
    isSelectionEmpty() {
      return (
        this.getIsElementSelectionEmpty &&
        this.getIsConnectionSelectionEmpty &&
        this.getIsNoteSelectionEmpty
      );
    },

    /**
     * Return whether there is a board currently dragged over the jumper
     * @returns {Boolean}
     */
    isBoardElementDraggedOver() {
      const { type } = this.getItems;
      return (
        this.dragEnter > 0 &&
        (type === DRAG_TYPE.BOARD_ELEMENT || type === DRAG_TYPE.SEARCH_ELEMENT)
      );
    },

    /**
     * Return whether there is some new item dragged over the jumper
     * @returns {Boolean}
     */
    isNewItemDraggedOver() {
      const { type } = this.getItems;
      return this.dragEnter && DRAG_TYPES_NEW.includes(type);
    },

    classObject() {
      const { theme } = this.element || { theme: '' };
      return {
        [theme]: true,
        'board-element-drag-over': this.isBoardElementDraggedOver,
        'newitem-drag-over': this.isNewItemDraggedOver,
        'jtk-drag-selected': this.isSelected,
        'user-selected': this.selectedByCollaborator !== null,
        'virtual-selected': this.virtualSelected,
      };
    },
  },
  watch: {
    /**
     * Watch selection change to inform jsplumb and enable-disable resizable.
     */
    isSelected(newVal, oldVal) {
      if (!this.canEdit) return;
      if (newVal) {
        jspSingleton.instance.addToDragSelection(this.$el); // Add it to jsplumb selection
      } else {
        jspSingleton.instance.removeFromDragSelection(this.$el);
      }
    },
    /**
     * Some property in the element style changes
     */
    styles(newVal, oldVal) {
      this.$nextTick(() => {
        jspSingleton.instance.revalidate(this.$el); // we need to repaint the element's connections
      });
    },

    'element.title': function elementTitle(newVal, oldVal) {
      this.$nextTick(() => {
        jspSingleton.instance.revalidate(this.$el); // we need to repaint the element's connections
      });
    },

    'element.boardName': function elementBoardName(newVal, oldVal) {
      this.$nextTick(() => {
        jspSingleton.instance.revalidate(this.$el); // we need to repaint the element's connections
      });
    },

    /**
     * There is nothing being dragged
     */
    getIsEmpty(newVal, oldVal) {
      if (newVal) {
        // Clear any drag enter
        this.dragEnter = false;
      }
    },
  },

  /**
   * Lifecycle hook, the element has been mounted
   */
  mounted() {
    jspSingleton.instance.setDraggable(this.$el, this.canEdit);
    // Make jumper draggable and resizable only if it is editable
    if (!this.canEdit) return;

    // These listeners have to be added explicitly since vddl consumes them
    this.$el.addEventListener('dragenter', this.onDragEnter);
    this.$el.addEventListener('dragleave', this.onDragLeave);

    // Add it to the managed list to make it draggable
    jspSingleton.instance.manage(this.$el);

    if (this.isSelected) {
      jspSingleton.instance.addToDragSelection(this.$el);
    }
  },

  /**
   * Lifecycle hook, before destroy
   */
  beforeUnmount() {
    this.$el.removeEventListener('dragenter', this.onDragEnter);
    this.$el.removeEventListener('dragleave', this.onDragLeave);

    // Remove it from the managed list
    jspSingleton.instance.unmanage(this.$el, false);
  },

  methods: {
    ...mapActions('project/config/boards', ['setOpen']),
    ...mapActions('application/drag', ['setTarget']),
    ...mapActions('application/contextToolbar', [
      'setAnchor',
      'recalcAnchorRect',
    ]),
    ...mapActions('project/data/elements', { updateElement: 'customUpdate' }),
    ...mapActions('project/data/jumpers', { updateJumper: 'customUpdate' }),
    ...mapActions('project/data/notes', { updateNote: 'customUpdate' }),
    ...mapActions('project/data/branches', { updateBranch: 'customUpdate' }),
    ...mapActions('project/data/comments', { updateComment: 'customUpdate' }),
    ...mapActions('project/config/jumpers', {
      selectJumper: 'select',
      deselectJumper: 'deselect',
    }),
    ...mapActions('project/config', ['deselectAll', 'deleteSelections']),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
    ]),
    ...mapGetters('application/clipboard', ['getCanPaste', 'getClipboard']),

    /**
     * Event handler for click event.
     */
    onClickTitle() {
      // if no element exists do nothing
      if (!this.element) return;
      emitter.emit(EVENT.ZOOMTONODE, {
        node: this.element,
        type: 'elements',
        boardId: this.element.boardId,
      });
    },

    /**
     * Event listener - something is dropped on the jumper (vddl drop event)
     */
    onJumperDropped(event) {
      console.log('OnJumperDropped');
      console.log(this.id);
      this.setTarget({
        value: {
          type: DRAG_LIST.JUMPERS,
          id: this.id,
          index: '-',
        },
      });
    },

    /**
     * Event listener - mouse up event on element. We will handle element selection here.
     * @param {MouseEvent} event
     */
    onMouseUp(event) {
      const { target, button, ctrlKey, shiftKey, metaKey } = event;
      // Ignore mouse up if occured on a the element link
      if (target.closest('.title.active')) {
        return;
      }

      const isDragged = jspSingleton.instance.hasClass(this.$el, 'jtk-drag');
      // Perform the selection sequence if left  or right single click and user can edit
      if (!isDragged && this.canEdit && button !== 1) {
        if (!this.$el.contains(document.activeElement)) {
          this.removeAllRanges();
        }
        // When Ctrl or Shift pressed toggle selection
        if (ctrlKey || shiftKey || metaKey) {
          if (!this.isSelected) {
            this.selectJumper({ id: this.id, apply: true });
          } else {
            this.deselectJumper({ id: this.id, apply: true });
          }
        } else if (!this.isSelected) {
          // Clear any selections and select only this element
          this.deselectAll({ apply: true });
          jspSingleton.instance.clearDragSelection();

          this.selectJumper({ id: this.id, apply: true });
        }
      }
    },

    /**
     * Event listener - contextmenu event.
     */
    onContextMenu(event) {
      if (!this.canEdit) return;
      // Select the jumper
      if (!this.isSelected) {
        this.deselectAll({ apply: false });
        // Clear the selections from jsplumb
        jspSingleton.instance.clearDragSelection();
        this.selectJumper({ id: this.id, apply: true });
      }
      // Show the context menu
      this.createContextMenu(event.clientX, event.clientY);
    },

    /**
     * Create a context menu for this element
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      const ids = this.getJumperSelections;

      const clipboard = this.getClipboard();
      this.createItem({
        name: 'Paste as Jumper',
        icon: null,
        commands: {
          doAction: () => {
            this.updateJumper({
              id: this.id,
              props: { elementId: clipboard.data.elements[0].id },
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => {
            return (
              ids.includes(this.id) &&
              ids.length === 1 &&
              clipboard.data.elements.length === 1 &&
              clipboard.operation === OPERATION.COPY &&
              clipboard.type === TYPE.ELEMENTS
            );
          },
          isEnabled: () => {
            return (
              ids.includes(this.id) &&
              ids.length === 1 &&
              clipboard.data.elements.length === 1 &&
              clipboard.operation === OPERATION.COPY &&
              clipboard.type === TYPE.ELEMENTS
            );
          },
        },
        submenu: null,
      });

      this.createItem({
        name: 'Delete selection',
        icon: null,
        commands: {
          doAction: () => {
            this.deleteSelections({ apply: true });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });
    },

    /**
     * Event listener - dragenter event something is dragged inside the element
     */
    onDragEnter(event) {
      this.dragEnter = true;
    },

    /**
     * Event listener - dragleave event something dragged is leaving the element
     */
    onDragLeave(event) {
      const rect = this.$el.getBoundingClientRect();
      if (
        event.clientY < rect.top ||
        event.clientY >= rect.bottom ||
        event.clientX < rect.left ||
        event.clientX >= rect.right
      ) {
        this.dragEnter = false;
      }
    },

    /**
     * Event hanlder: note mouse down event.
     * @param {MouseEvent} event
     */
    onMouseDown(event) {
      // On middle-click do not start drag
      if (event.button === 1) {
        event.preventDefault();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.jumper {
  position: absolute;
  box-sizing: border-box;
  font-size: 16px;
  color: #bcbcbc;
  // border-radius: 8px;
  display: flex;
  // background-color: #0d0e0e;
}

.jumper .wrapper {
  display: flex;
  width: 100%;
  background: #0d0e0e;
  border-radius: 8px;
  overflow: hidden;
}

.jumper.jtk-drag-selected.jtk-drag-hover::before,
.jumper.jtk-drag-hover::before,
.jumper.jtk-drag-selected.board-element-drag-over::before,
.jumper.board-element-drag-over::before {
  content: '';
  z-index: -1;
  position: absolute;
  left: -3px;
  top: -3px;
  right: -3px;
  bottom: -3px;
  border: none;
  background: #db841e;
  border-radius: 10px;
  // background-image: repeating-linear-gradient(
  //   45deg,
  //   transparent,
  //   transparent 10px,
  //   rgba(30, 33, 33, 1) 10px,
  //   rgba(30, 33, 33, 1) 20px
  // );
  // background-size: 30px;
  // animation: spin 10s linear infinite;
}

.jumper.jtk-drag-selected.jtk-drag-hover .handler::after,
.jumper.jtk-drag-selected.board-element-drag-over .handler::after {
  display: none;
}

@keyframes spin {
  100% {
    background-position: 500px 0;
  }
}

.jumper .handler {
  display: flex;
  flex-direction: row;
  flex: 1;
  // overflow:none;
  // border-radius:8px;
  // overflow: hidden;
  // position: relative;
  // border-radius: 8px 0 0 8px;
  // background: #0d0e0e;
}
.jumper .svg-icon-container {
  // position: absolute;
  // right: -12px;
  // background: transparent;
  // height: 70px;
  // z-index: 1;
}
.jumper .top {
  // height: 16px;
  // border-radius: 8px 8px 0 0;
  position: absolute;
  right: -8px;
  width: 8px;
  background: #555 !important;
  height: 72px;
  z-index: 1;
  // border-radius: 0 3px 3px 0;

  // background: repeating-linear-gradient(
  //   45deg,
  //   #2c3233,
  //   #2c3233 6px,
  //   #252929 6px,
  //   #252929 12px
  // );
  .arrow {
    width: 0;
    height: 0;
    border-top: 36px solid transparent;
    border-left: 12px solid #555;
    border-bottom: 36px solid transparent;
    background: transparent !important;
    position: absolute;
    right: -11px;
  }
}

.jumper .row {
  display: flex;
  align-items: center;
  padding: 8px 0 7px 0;
}

.jumper .title {
  justify-content: flex-start;
  font-weight: 600;
  font-size: 16px;
  // flex-grow: 1;
  overflow: hidden;
  min-width: 0;
  text-align: left;
  // min-height: 22px;
  // width: 100%;
  color: #db841e;
  cursor: pointer !important;
  &:hover {
    text-decoration: underline;
    cursor: pointer !important;
  }
  &.empty {
    color: #555656;
    text-decoration: none !important;
    cursor: default !important;
  }
}

.jumper .container {
  position: relative;
  overflow: hidden;
  margin: 9px 20px 9px 16px;
  padding: 0 0 0 0;
  display: flex;
  flex-flow: column;
  cursor: default;
  &.empty {
    margin: 9px 20px 9px 20px;
  }
  .board {
    font-size: 14px;
    margin-bottom: 8px;
    span {
      color: rgba(255, 255, 255, 0.3);
    }
  }
}

// We temporarily disable the item to allow vddl drop on the parent (board)
.jumper.newitem-drag-over {
  pointer-events: none;
}

// .jumper:hover {
//   cursor: crosshair;
// }

/*** ANIMATIONS ***/

.bounce-enter-active {
  animation: bounce-in 0.3s;
  //   transition: opacity .5s;
}
.bounce-leave-active {
  animation: bounce-in 0.3s reverse;
  //   transition: opacity .5s;
}
@keyframes bounce-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>

<style lang="scss">
.jumper > .vddl-placeholder {
  display: none;
}

// @keyframes flickerAnimation {
//   /* flame pulses */
//   0% {
//     opacity: 1;
//   }
//   50% {
//     opacity: 0;
//   }
//   100% {
//     opacity: 1;
//   }
// }

/* ENDPOINTS */

// .jtk-endpoint.source circle {
//   transition: all 0.2s;
// }

// .jtk-endpoint.source.jtk-endpoint-connected.jtk-dragging {
//   visibility: hidden !important;
// }

// .jtk-endpoint.source.endpointDrag.jtk-drag {
//   visibility: hidden !important;
// }

// .jtk-endpoint.source:hover {
//   z-index: 100;
// }

// .jtk-endpoint.source:hover circle {
//   fill: #db841e;
// }

// While dragging element do not fire any clicking on mentions
.jumper.jtk-drag .title {
  pointer-events: none;
}
</style>
